// src/components/usecases/EnglishAI_Posting.js
import React from 'react';
import './englishAI_posting.css';

import eng1 from '../../assets/eng1.png';
import eng2 from '../../assets/eng2.png';
import eng3 from '../../assets/eng3.png';
import eng4 from '../../assets/eng4.png';
import eng5 from '../../assets/eng5.png';
import eng6 from '../../assets/eng6.png';

export default function EnglishAI_Posting() {
  return (
    <div className="english-ai-page">
      {/* Main Content */}
      <div className="content-container">
        <h1 className="main-title">
          AI-Driven Practice and Feedback System for Enhanced Language Learning
        </h1>
        <h2 className="sub-title">
          From Real-Time Exam Simulation to Personalized AI Feedback and Progress Tracking
        </h2>

        <p className="intro-text">
          In the modern educational landscape, effective learning is no longer confined to traditional lectures and static textbooks. Instead, dynamic, interactive, and data-driven systems are reshaping how students engage with knowledge—especially in language learning. This system presents a comprehensive and intelligent student experience that combines test-taking with personalized feedback, active learning support, and detailed progress tracking.
        </p>

        {/* UI/UX Flow Images Centered Mid-Content */}
        <div className="flow-wrapper">
          <div className="flow-container">
            {[eng1, eng2, eng3, eng4, eng5, eng6].map((src, idx) => (
              <img
                key={idx}
                src={src}
                alt={`Flow step ${idx + 1}`}
                className="flow-image"
              />
            ))}
          </div>
        </div>

        <section className="section">
          <h3>🧠 User Experience 1: Intelligent Exam Simulation and Interaction</h3>

          <div className="subsection">
            <h4>1. Mock Test Generation</h4>
            <ul>
              <li>Students begin by taking a simulated test of 10–12 multiple-choice questions, dynamically drawn from a curated exam database.</li>
              <li>Question pool updates continuously to reflect evolving exam patterns.</li>
              <li>Tests tailor to difficulty levels or themes (e.g., TOEFL reading, SAT vocabulary).</li>
            </ul>
          </div>

          <div className="subsection">
            <h4>2. Interactive Answering Interface</h4>
            <ul>
              <li>Digital annotation tools (underline, circle, highlight, check) replicate tactile interaction.</li>
              <li>Markings save for post-test review to track thinking processes.</li>
              <li>Interaction data analyzes learner behavior for deeper insights.</li>
            </ul>
          </div>

          <div className="subsection">
            <h4>3. Automatic Grading</h4>
            <ul>
              <li>System instantly scores objective questions, providing real-time feedback.</li>
              <li>Automated grading ensures consistency and reduces instructor workload.</li>
              <li>Flags incorrect answers for AI-driven remedial learning.</li>
            </ul>
          </div>
        </section>

        <section className="section">
          <h3>📚 User Experience 2: Targeted AI Feedback and Remedial Learning</h3>

          <div className="subsection">
            <h4>1. Vocabulary Reinforcement</h4>
            <ul>
              <li>Identifies challenging words and offers AI-generated synonyms and example sentences.</li>
              <li>Content tailors to learner level for clarity and fluency.</li>
            </ul>
          </div>

          <div className="subsection">
            <h4>2. Sentence Interpretation Practice</h4>
            <ul>
              <li>Generates new multiple-choice questions based on similar sentence structures.</li>
              <li>Reinforces comprehension and critical thinking.</li>
            </ul>
          </div>

          <div className="subsection">
            <h4>3. Similar Question Practice</h4>
            <ul>
              <li>Fetches teacher-validated questions from the database for pedagogical alignment.</li>
            </ul>
          </div>
        </section>

        <section className="section">
          <h3>📊 Dashboard & Progress Tracking</h3>
          <ul>
            <li><strong>Assignment Completion Rate:</strong> Visual metrics on study consistency.</li>
            <li><strong>Score Trends:</strong> Performance graphs to highlight improvement areas.</li>
            <li><strong>Feedback History:</strong> Log of AI explanations and exercises for review.</li>
          </ul>
        </section>

        <section className="section">
          <h3>Conclusion</h3>
          <p>
            By transforming incorrect answers into adaptive practice and tracking growth over time, this intelligent system creates a powerful ecosystem for self-directed language learning and measurable academic growth.
          </p>
        </section>
      </div>
    </div>
  );
}